import React from 'react';
import BlendSlider from './BlendSlider';
import TempoField from './TempoField';
import strings from './strings';
import { PlayIcon, PauseIcon, StopIcon, MetronomeIcon, MagnifierIcon } from './icons';

/**
 * The SETUP dock: everything a singer sets before pressing play.
 * Purely presentational; the shell owns the behaviour.
 *
 * variant 'inline' is the full dock under the score. variant 'sheet' is the
 * slide-up version above the practice strip: the strip already shows the
 * blend slider and stop/play/pause, so those are left out here.
 *
 * `magnify` replaces the Size percentage with a Magnify toggle set a
 * little apart from the - and + buttons: related, but its own thing.
 */
const MobileDock = ({ variant = 'inline', blend, blendPending, show, zoom, transport, tempo, section, magnify = null }) => {
  const inSheet = variant === 'sheet';

  return (
    <div className={`mps-dock ${variant}`}>
      {section && section.start !== null && (
        <div className="mps-section-row" role="status">
          <span className="mps-section-text">
            &#128257; {section.end === null || section.end === section.start
              ? `${strings.sectionBar} ${section.start}`
              : `${strings.sectionBars} ${section.start}–${section.end}`}
            {section.end === null ? ` · ${strings.sectionFromTap}` : ` · ${section.once ? strings.sectionOnce : strings.sectionLooping}`}
          </span>
          <button type="button" className="mps-section-clear" onClick={section.onClear} disabled={section.locked} aria-label={strings.clearSelection}>
            &times;
          </button>
        </div>
      )}
      {!inSheet && (
        <div className={blend.dimmed ? 'mps-dimmed' : undefined} title={blend.dimmed ? strings.partsOverrideOn : undefined}>
          <BlendSlider
            value={blend.value}
            onChange={blend.onChange}
            disabled={blend.disabled}
            pending={blendPending}
            onDisabledTap={blend.onPickPart}
          />
        </div>
      )}

      <div className="mps-row">
        <div className={`mps-seg ${show.dimmed ? 'mps-dimmed' : ''}`} role="group" aria-label={strings.show} title={show.dimmed ? strings.partsOverrideOn : undefined}>
          <span className="mps-seg-label">{strings.show}</span>
          <button
            type="button"
            className={show.mode === 'mine' ? 'active' : ''}
            aria-pressed={show.mode === 'mine'}
            onClick={show.onMine}
          >
            {strings.showMine}
          </button>
          <button
            type="button"
            className={show.mode === 'all' ? 'active' : ''}
            aria-pressed={show.mode === 'all'}
            onClick={show.onAll}
          >
            {strings.showAll}
          </button>
          {show.mode === 'custom' && <span className="mps-seg-custom">{strings.showCustom}</span>}
        </div>

        <div className="mps-field">
          <span className="mps-field-label">{strings.zoomLabel}</span>
          <div className="mps-size">
            <div className="mps-stepper" aria-label={strings.zoomLabel}>
              <button type="button" onClick={zoom.onOut} disabled={!zoom.canOut} aria-label={strings.zoomOut}>&minus;</button>
              {!magnify && <span className="mps-stepper-value">{Math.round(zoom.value * 100)}%</span>}
              <button type="button" onClick={zoom.onIn} disabled={!zoom.canIn} aria-label={strings.zoomIn}>+</button>
            </div>
            {magnify && (
              <button
                type="button"
                className={`mps-magnify ${magnify.on ? 'active' : ''}`}
                onClick={magnify.onToggle}
                disabled={!magnify.available}
                aria-pressed={magnify.on}
                aria-label={strings.magnify}
                title={magnify.available ? strings.magnify : strings.lyricsNone}
              >
                <MagnifierIcon size={22} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mps-row mps-transport">
        {!inSheet && (
          <button
            type="button"
            className="mps-round mps-stop"
            onClick={transport.onStop}
            disabled={!transport.canStop}
            aria-label={strings.stop}
          >
            <StopIcon size={20} />
          </button>
        )}
        {!inSheet && (
          <button
            type="button"
            className="mps-round mps-play"
            onClick={transport.isPlaying ? transport.onPause : transport.onPlay}
            disabled={transport.isPlaying ? !transport.canPause : !transport.canPlay}
            aria-label={transport.isPlaying ? strings.pause : strings.play}
          >
            {transport.isPlaying ? <PauseIcon size={28} /> : <PlayIcon size={28} />}
          </button>
        )}

        <div className="mps-field">
          <span className="mps-field-label">{strings.tempoLabel} <span className="mps-field-unit">({strings.bpmUnit})</span></span>
          <div className="mps-stepper mps-tempo">
            <button type="button" onClick={() => tempo.onAdjust(-1)} disabled={!tempo.bpm} aria-label={strings.slower}>&minus;</button>
            {tempo.bpm
              ? <TempoField value={tempo.bpm} onCommit={tempo.onSet} />
              : <span className="mps-tempo-field">--</span>}
            <button type="button" onClick={() => tempo.onAdjust(1)} disabled={!tempo.bpm} aria-label={strings.faster}>+</button>
          </div>
        </div>

        <button
          type="button"
          className={`mps-round mps-metronome ${tempo.metronome ? 'active' : ''} ${tempo.beat ? `beat ${tempo.beat}` : ''}`}
          aria-pressed={tempo.metronome}
          aria-label={strings.metronome}
          onClick={() => tempo.onToggleMetronome(!tempo.metronome)}
        >
          <MetronomeIcon size={22} />
        </button>
      </div>
    </div>
  );
};

export default MobileDock;
